import { useEffect, useState } from "react";
import { Button, Form } from "react-bootstrap";
import sharedProperty from "../../../../app/sharedProperty";
import { showErrorMessage, showSuccessMessage } from "../../../../app/tools";
import { Icon, IconDefinitions, InfoSection, SlideOutModal } from "../../../../components";
import { GaugeConfig, PeakSignalModel, PeakSignalUpdateModel } from "../../../../data/entities";
import { SeverityBadge, buildSignalDetails } from "./peakSignalDetails";
import { SIGNAL_LABELS, formatNumber } from "./peakSignalFormat";

interface EditPeakSignalModalProps {
  showState: sharedProperty<boolean>;
  signal: PeakSignalModel | null;
  config: GaugeConfig | null;
  onSave: (signal: PeakSignalModel, changes: PeakSignalUpdateModel) => void | Promise<void>;
}

const parsePosition = (draft: string): number | null | undefined => {
  if (draft.trim() === "") return null;
  const parsed = Number(draft);
  return Number.isFinite(parsed) ? parsed : undefined;
};

export function EditPeakSignalModal({ showState, signal, config, onSave }: EditPeakSignalModalProps) {
  const [draftValue, setDraftValue] = useState("");
  const [draftStart, setDraftStart] = useState("");
  const [draftEnd, setDraftEnd] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (showState.value && signal) {
      setDraftValue(String(signal.peakValue));
      setDraftStart(signal.startPosition != null ? String(signal.startPosition) : "");
      setDraftEnd(signal.endPosition != null ? String(signal.endPosition) : "");
    }
  }, [showState.value, signal]);

  if (!signal) return null;

  const parsed = Number(draftValue);
  const isValueValid = draftValue.trim() !== "" && Number.isFinite(parsed) && parsed > 0;

  const startPosition = parsePosition(draftStart);
  const endPosition = parsePosition(draftEnd);
  const positionsOutOfOrder =
    startPosition != null && endPosition != null && endPosition < startPosition;
  const arePositionsValid =
    startPosition !== undefined && endPosition !== undefined && !positionsOutOfOrder;

  const isValid = isValueValid && arePositionsValid;
  const isUnchanged =
    isValid &&
    parsed === signal.peakValue &&
    startPosition === signal.startPosition &&
    endPosition === signal.endPosition;

  const handleSave = async () => {
    if (!isValid || isUnchanged) return;
    setSaving(true);
    try {
      await onSave(signal, {
        peakValue: parsed,
        startPosition: startPosition ?? null,
        endPosition: endPosition ?? null,
      });
      showSuccessMessage("Peak signal updated");
      showState.setter(false);
    } catch (error) {
      console.error("Error updating peak signal:", error);
      showErrorMessage("Failed to update peak signal");
    } finally {
      setSaving(false);
    }
  };

  return (
    <SlideOutModal
      showState={showState}
      title="Edit Peak Signal"
      footer={
        <div className="row m-0 w-100">
          <div className="col-6 ps-0">
            <Button
              onClick={() => showState.setter(false)}
              variant="cancel"
              className="w-100"
              disabled={saving}
            >
              Cancel
            </Button>
          </div>
          <div className="col-6 pe-0">
            <Button
              onClick={handleSave}
              variant="primary"
              className="w-100"
              disabled={saving || !isValid || isUnchanged}
            >
              {saving ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </Button>
          </div>
        </div>
      }
    >
      <InfoSection title="Peak Signal" details={buildSignalDetails(signal, config, false)} />

      <Form.Group className="mb-3">
        <Form.Label className="fw-semibold">Peak Value</Form.Label>
        <Form.Control
          type="number"
          step="any"
          min={0}
          value={draftValue}
          autoFocus
          onChange={(e) => setDraftValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleSave();
          }}
        />
        {config && (
          <Form.Text className="text-muted">
            Current {SIGNAL_LABELS[signal.signalType]} gauge: Warning from{" "}
            {formatNumber(config.cautionFlawValue)}, Alert from{" "}
            {formatNumber(config.criticalFlawValue)}
          </Form.Text>
        )}
        {config && isValueValid && parsed !== signal.peakValue && (
          <div className="d-flex align-items-center gap-2 mt-2">
            <small className="text-muted">New severity:</small>
            <SeverityBadge value={parsed} config={config} />
          </div>
        )}
      </Form.Group>

      <div className="row">
        <Form.Group className="mb-3 col-6">
          <Form.Label className="fw-semibold">Start Position</Form.Label>
          <Form.Control
            type="number"
            step="any"
            value={draftStart}
            isInvalid={startPosition === undefined || positionsOutOfOrder}
            onChange={(e) => setDraftStart(e.target.value)}
          />
        </Form.Group>
        <Form.Group className="mb-3 col-6">
          <Form.Label className="fw-semibold">End Position</Form.Label>
          <Form.Control
            type="number"
            step="any"
            value={draftEnd}
            isInvalid={endPosition === undefined || positionsOutOfOrder}
            onChange={(e) => setDraftEnd(e.target.value)}
          />
        </Form.Group>
        {positionsOutOfOrder && (
          <div className="col-12">
            <small className="text-danger d-block mb-3">
              End position must be greater than or equal to start position.
            </small>
          </div>
        )}
      </div>

      <div className="alert alert-secondary py-2 mb-0">
        <small className="d-flex">
          <span className="me-2 flex-shrink-0">
            <Icon iconName={IconDefinitions.circleInfo} />
          </span>
          <span>
            Changing these values updates the signal's severity and recalculates the
            inspection's peak gauges.
          </span>
        </small>
      </div>
    </SlideOutModal>
  );
}
